<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  Search,
  FileText,
  Paperclip,
  Monitor,
  ListTodo,
  CalendarDays,
  ChevronRight,
  CheckSquare,
  XSquare,
  Loader2,
} from 'lucide-vue-next'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useCopilotStore } from '../store'
import { useProjectsStore } from '@/views/Projects/store'
import ContextTreeItem from './ContextTreeItem.vue'
import ProjectSummaryCard from './ProjectSummaryCard.vue'

const copilotStore = useCopilotStore()
const projectsStore = useProjectsStore()

const {
  selectedProjectId,
  projectContext,
  contextItems,
  selectedItemIds,
  isLoadingContext,
  isExtractingContent,
} = storeToRefs(copilotStore)

const {
  projectStats,
  contextSummary,
  availableItemsByType,
  totalSelectedTokenEstimate,
} = storeToRefs(copilotStore)

const projectSearchOpen = ref(false)
const projectSearch = ref('')
const extractingId = ref<string | null>(null)

// Group sections state
const openSections = ref<Record<string, boolean>>({
  document: true,
  attachment: true,
  application: true,
  requirement: true,
  meeting: true,
})

const filteredProjects = computed(() => {
  const search = projectSearch.value.toLowerCase()
  if (!search) return projectsStore.projects
  return projectsStore.projects.filter(
    (p) =>
      p.name.toLowerCase().includes(search) ||
      p.description?.toLowerCase().includes(search)
  )
})

const sections = computed(() => [
  {
    type: 'document',
    label: 'Documentos',
    icon: FileText,
    items: availableItemsByType.value.document || [],
  },
  {
    type: 'attachment',
    label: 'Adjuntos',
    icon: Paperclip,
    items: availableItemsByType.value.attachment || [],
  },
  {
    type: 'application',
    label: 'Aplicaciones',
    icon: Monitor,
    items: availableItemsByType.value.application || [],
  },
  {
    type: 'requirement',
    label: 'Requerimientos',
    icon: ListTodo,
    items: availableItemsByType.value.requirement || [],
  },
  {
    type: 'meeting',
    label: 'Reuniones',
    icon: CalendarDays,
    items: availableItemsByType.value.meeting || [],
  },
])

const handleSelectProject = async (projectId: string) => {
  projectSearchOpen.value = false
  await copilotStore.selectProject(projectId)
}

const handleToggleItem = (itemId: string) => {
  copilotStore.toggleItemSelection(itemId)
}

const handleExtract = async (itemId: string) => {
  const item = contextItems.value.find((i) => i.id === itemId)
  if (!item) return

  // We need the attachment URL and mimeType from the raw data
  // The attachment info is embedded in the item
  extractingId.value = itemId
  try {
    // Fetch attachment data to get URL and mimeType
    const { doc: firestoreDoc, getDoc } = await import('firebase/firestore')
    const { db } = await import('@/config/firebase')
    const attachDoc = await getDoc(firestoreDoc(db, 'attachments', itemId))
    if (attachDoc.exists()) {
      const data = attachDoc.data()
      await copilotStore.extractAttachmentContent(itemId, data.url, data.mimeType)
    }
  } catch (error) {
    console.error('Error extracting content:', error)
  } finally {
    extractingId.value = null
  }
}

const toggleSection = (type: string) => {
  openSections.value[type] = !openSections.value[type]
}
</script>

<template>
  <div class="flex flex-col h-full">
    <!-- Project Selector -->
    <div class="p-3 border-b space-y-3">
      <Popover v-model:open="projectSearchOpen">
        <PopoverTrigger as-child>
          <Button
            variant="outline"
            role="combobox"
            :aria-expanded="projectSearchOpen"
            class="w-full justify-between text-sm"
          >
            <span class="truncate">
              {{ projectContext ? projectContext.projectName : 'Seleccionar proyecto...' }}
            </span>
            <ChevronRight class="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent class="w-[280px] p-0 z-[200]" align="start">
          <Command>
            <CommandInput v-model="projectSearch" placeholder="Buscar proyecto..." />
            <CommandList>
              <CommandEmpty>No se encontraron proyectos.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  v-for="project in filteredProjects"
                  :key="project.id"
                  :value="project.name"
                  @select="handleSelectProject(project.id)"
                >
                  <span class="truncate">{{ project.name }}</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <!-- Project Summary -->
      <ProjectSummaryCard
        v-if="projectContext"
        :project-name="projectContext.projectName"
        :project-status="projectContext.projectStatus"
        :stats="projectStats"
      />
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingContext" class="flex-1 flex items-center justify-center">
      <div class="text-center space-y-2">
        <Loader2 class="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
        <p class="text-sm text-muted-foreground">Cargando contexto...</p>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!projectContext"
      class="flex-1 flex items-center justify-center p-4"
    >
      <p class="text-sm text-muted-foreground text-center">
        Selecciona un proyecto para cargar su contexto
      </p>
    </div>

    <!-- Context Tree -->
    <ScrollArea v-else class="flex-1">
      <div class="p-2">
        <template v-for="section in sections" :key="section.type">
          <Collapsible
            v-if="section.items.length > 0"
            :open="openSections[section.type]"
            class="mb-1"
          >
            <CollapsibleTrigger
              class="flex items-center gap-2 w-full px-2 py-1.5 text-sm font-medium hover:bg-muted/50 rounded-md"
              @click="toggleSection(section.type)"
            >
              <ChevronRight
                class="h-4 w-4 transition-transform"
                :class="{ 'rotate-90': openSections[section.type] }"
              />
              <component :is="section.icon" class="h-4 w-4 text-muted-foreground" />
              <span>{{ section.label }}</span>
              <Badge variant="secondary" class="ml-auto text-[10px] px-1.5 py-0 h-4">
                {{ section.items.length }}
              </Badge>
            </CollapsibleTrigger>
            <CollapsibleContent class="pl-4">
              <ContextTreeItem
                v-for="item in section.items"
                :key="item.id"
                :item="item"
                :selected="selectedItemIds.includes(item.id)"
                :is-extracting="extractingId === item.id"
                @toggle="handleToggleItem"
                @extract="handleExtract"
              />
            </CollapsibleContent>
          </Collapsible>
        </template>
      </div>
    </ScrollArea>

    <!-- Selection Summary -->
    <div v-if="projectContext" class="border-t p-3 space-y-2">
      <div class="flex items-center justify-between">
        <p class="text-xs text-muted-foreground">
          {{ selectedItemIds.length }} items seleccionados
        </p>
        <p class="text-xs text-muted-foreground">
          ~{{ totalSelectedTokenEstimate.toLocaleString() }} tokens
        </p>
      </div>
      <p class="text-xs text-muted-foreground truncate">{{ contextSummary }}</p>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          class="flex-1 text-xs h-7"
          @click="copilotStore.selectAllItems()"
        >
          <CheckSquare class="h-3 w-3 mr-1" />
          Todo
        </Button>
        <Button
          variant="outline"
          size="sm"
          class="flex-1 text-xs h-7"
          @click="copilotStore.deselectAllItems()"
        >
          <XSquare class="h-3 w-3 mr-1" />
          Limpiar
        </Button>
      </div>
    </div>
  </div>
</template>
